import { APP_TIME_ZONE } from '@crew/shared';

/**
 * עיצוב תאריכים בעברית. תאריך עבודה נשמר כ-YYYY-MM-DD בשעון ישראל,
 * ולכן ממירים אותו ל-Date ב-UTC ומעצבים ב-UTC — בלי הזזות של אזור זמן.
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseJobDate(date: string): Date | null {
  const m = DATE_RE.exec(date);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(d.getTime()) || d.getUTCDate() !== Number(m[3]) ? null : d;
}

/** התאריך של היום בישראל, כ-YYYY-MM-DD. */
export function todayInIsrael(now: Date = new Date()): string {
  // en-CA מחזיר בדיוק את הפורמט YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function addDays(date: string, days: number): string {
  const d = parseJobDate(date);
  if (!d) return date;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "יום שלישי, 20 באוקטובר" */
export function formatJobDate(date: string): string {
  const d = parseJobDate(date);
  if (!d) return date;
  return new Intl.DateTimeFormat('he-IL', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(d);
}

/** "היום" / "מחר" / "אתמול" או null */
export function relativeDayLabel(date: string, today: string = todayInIsrael()): string | null {
  if (date === today) return 'היום';
  if (date === addDays(today, 1)) return 'מחר';
  if (date === addDays(today, -1)) return 'אתמול';
  return null;
}

export function greetingFor(now: Date = new Date()): string {
  const hour = Number(new Intl.DateTimeFormat('en-GB', { timeZone: APP_TIME_ZONE, hour: '2-digit', hour12: false }).format(now));
  if (hour >= 5 && hour < 12) return 'בוקר טוב';
  if (hour >= 12 && hour < 17) return 'צהריים טובים';
  if (hour >= 17 && hour < 22) return 'ערב טוב';
  return 'לילה טוב';
}

export function formatLongToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('he-IL', { weekday: 'long', day: 'numeric', month: 'long', timeZone: APP_TIME_ZONE }).format(now);
}

/** שם חודש ושנה לכותרת לוח השנה. */
export function formatMonthTitle(year: number, monthIndex: number): string {
  return new Intl.DateTimeFormat('he-IL', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, monthIndex, 1)),
  );
}

/** תאי לוח חודשי שמתחיל ביום ראשון. null = תא ריק. */
export function monthGrid(year: number, monthIndex: number): Array<string | null> {
  const first = new Date(Date.UTC(year, monthIndex, 1));
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const cells: Array<string | null> = Array.from({ length: first.getUTCDay() }, () => null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export const WEEKDAY_SHORT_HE = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];
